import { useState, type FormEvent } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, FileText, Plus, Search } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import {
  Cabecalho,
  Celula,
  CLASSE_DE_CAMPO,
  Linha,
  LinhaDeCabecalho,
} from '@/features/admin/componentes';
import {
  chavesDeContratos,
  exportarContratosCSV,
  listarContratos,
  listarSobreposicoes,
  retomarContrato,
  suspenderContrato,
  type ContractStatus,
  type ContractSummary,
  type ContractType,
  type FiltroDeContratos,
} from '@/features/contratos/contratos.api';
import { Aviso, DialogoDeContrato, Etiqueta } from '@/features/contratos/componentes';
import {
  enderecoDoContrato,
  SITUACOES_DE_CONTRATO,
  situacaoDeContrato,
  textoDeVencimento,
  TIPOS_DE_CONTRATO,
  tipoDeContrato,
  tomDeVencimento,
} from '@/features/contratos/navegacao';
import { formatarData } from '@/lib/format';

/**
 * A carteira de contratos.
 *
 * ## Vencidos aparecem, e em vermelho
 *
 * A lista sai ordenada por **quem vence primeiro**, e o contrato vencido fica
 * no topo em vez de escondido. É a resposta de tela ao defeito medido no
 * sistema anterior: lá, 32 dos 63 contratos com situação dizem "Vigente" e já
 * venceram, porque o campo é digitado à mão e ninguém volta para corrigi-lo.
 * Aqui a situação é **derivada das datas** — a coluna não existe no banco —, e
 * quem abre esta tela vê a verdade sem precisar conferir contrato a contrato.
 *
 * ## A fila de sobreposições fica no topo, não numa aba
 *
 * Termos sobrepostos são o bug que faz dois ANS valerem ao mesmo tempo. O
 * banco impede que dois entrem em vigor, mas os que vieram do sistema anterior
 * chegam parados, esperando decisão humana. Um aviso na tela principal é o que
 * garante que essa fila não fique esquecida numa aba que ninguém abre.
 */
export function ContratosPage() {
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [status, setStatus] = useState<ContractStatus | ''>('');
  const [tipo, setTipo] = useState<ContractType | ''>('');
  const [soVencendo, setSoVencendo] = useState(false);
  const [criando, setCriando] = useState(false);

  const filtro: FiltroDeContratos = {
    ...(buscaAplicada ? { q: buscaAplicada } : {}),
    ...(status ? { status } : {}),
    ...(tipo ? { type: tipo } : {}),
    ...(soVencendo ? { vencendoEm: 60 } : {}),
  };

  const consulta = useQuery({
    queryKey: chavesDeContratos.lista(filtro),
    queryFn: () => listarContratos(filtro),
    // Sem isto a tabela pisca para o esqueleto a cada troca de filtro, e a
    // pessoa perde a referência de onde estava na lista.
    placeholderData: keepPreviousData,
  });

  const sobreposicoes = useQuery({
    queryKey: chavesDeContratos.sobreposicoes,
    queryFn: listarSobreposicoes,
  });

  const contratos = consulta.data?.items ?? [];
  const pendentes = sobreposicoes.data?.items ?? [];

  const qc = useQueryClient();
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());

  const toggleSelecionado = (id: string) =>
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const toggleTodos = () =>
    setSelecionados((prev) =>
      prev.size === contratos.length ? new Set() : new Set(contratos.map((c) => c.id)),
    );

  const acaoEmLote = useMutation({
    mutationFn: async (acao: 'suspend' | 'resume') => {
      const ids = [...selecionados];
      for (const id of ids) {
        if (acao === 'suspend') await suspenderContrato(id, { reason: 'Suspensão em lote' });
        else await retomarContrato(id, {});
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chavesDeContratos.raiz });
      setSelecionados(new Set());
    },
  });
  const filtrando = buscaAplicada !== '' || status !== '' || tipo !== '' || soVencendo;

  return (
    <>
      <div className="mx-auto max-w-[1400px] space-y-3 animate-fade-in">
        <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
          <h1 className="text-base font-semibold">Contratos</h1>
          <div className="flex items-center gap-2">
            <a
              href={exportarContratosCSV(filtro)}
              download="contratos.csv"
              className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Exportar CSV
            </a>
            <Button
              size="sm"
              onClick={() => setCriando(true)}
              icon={<Plus className="h-4 w-4" aria-hidden />}
            >
              Novo contrato
            </Button>
          </div>
        </header>

        {pendentes.length > 0 ? (
          <Aviso
            tom="perigo"
            titulo={`${pendentes.length} termo(s) em rascunho se sobrepõem a um termo em vigor`}
          >
            <p>
              Dois compromissos válidos ao mesmo tempo no mesmo contrato é o que o sistema não
              deixa acontecer — e por isso estes ficaram parados, esperando decisão de uma pessoa.
              Abra o contrato e ative o termo certo, dizendo qual dos dois ele substitui.
            </p>
            <ul className="mt-2 space-y-1">
              {pendentes.map((pendente) => (
                <li key={pendente.termId}>
                  <Link
                    {...enderecoDoContrato(pendente.contractId)}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    {pendente.contractNumber} — {pendente.contractName}
                  </Link>
                  <span className="text-muted-foreground">
                    {' '}
                    · termo {pendente.sequence} ({formatarData(pendente.startsAt)} →{' '}
                    {pendente.endsAt ? formatarData(pendente.endsAt) : 'indeterminado'}) colide com{' '}
                    {pendente.colideCom.map((c) => `termo ${c.sequence}`).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          </Aviso>
        ) : null}

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(evento: FormEvent) => {
            evento.preventDefault();
            setBuscaAplicada(busca.trim());
          }}
        >
          <div className="min-w-[16rem] flex-1 space-y-1.5">
            <label htmlFor="contratos-busca" className="text-sm font-medium">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                id="contratos-busca"
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                placeholder="Nome, número interno ou número do cliente"
                className={`${CLASSE_DE_CAMPO} pl-9`}
              />
            </div>
          </div>

          <div className="w-48 space-y-1.5">
            <label htmlFor="contratos-status" className="text-sm font-medium">
              Situação
            </label>
            <select
              id="contratos-status"
              value={status}
              onChange={(evento) => setStatus(evento.target.value as ContractStatus | '')}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">Todas</option>
              {SITUACOES_DE_CONTRATO.map((situacao) => (
                <option key={situacao.valor} value={situacao.valor}>
                  {situacao.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div className="w-52 space-y-1.5">
            <label htmlFor="contratos-tipo" className="text-sm font-medium">
              Tipo
            </label>
            <select
              id="contratos-tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as ContractType | '')}
              className={CLASSE_DE_CAMPO}
            >
              <option value="">Todos</option>
              {TIPOS_DE_CONTRATO.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={soVencendo}
              onChange={(evento) => setSoVencendo(evento.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            Vencendo em 60 dias
          </label>

          <Button type="submit" variant="secondary" size="md">
            Filtrar
          </Button>
        </form>

        {consulta.data?.truncado ? (
          <Aviso tom="alerta" titulo="A lista está incompleta">
            <p>
              A carteira tem mais contratos do que uma leitura alcança, e os filtros de situação e
              de vencimento são aplicados sobre o que foi lido. Estreite a busca por cliente, tipo
              ou texto antes de concluir que um contrato não existe.
            </p>
          </Aviso>
        ) : null}

        <Card>
          <CardBody className="p-0">
            {consulta.isError ? (
              <div className="px-5 py-6">
                <ErrorState
                  error={consulta.error}
                  title="Não foi possível carregar os contratos"
                  onRetry={() => void consulta.refetch()}
                />
              </div>
            ) : consulta.isPending ? (
              <SkeletonRegion label="Carregando os contratos" className="space-y-3 px-5 py-5">
                <SkeletonText lines={5} />
              </SkeletonRegion>
            ) : contratos.length === 0 ? (
              <div className="px-5 py-12">
                <EmptyState
                  icon={<FileText className="h-7 w-7" aria-hidden />}
                  title={filtrando ? 'Nenhum contrato com esse filtro' : 'Nenhum contrato cadastrado'}
                  description={
                    filtrando
                      ? 'Limpe os filtros para ver a carteira inteira — inclusive os vencidos e os rescindidos.'
                      : 'Cadastre o primeiro contrato. Sem contrato, o chamado abre normalmente e nasce marcado como "sem acordo".'
                  }
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <LinhaDeCabecalho>
                    <Cabecalho>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border"
                        checked={contratos.length > 0 && selecionados.size === contratos.length}
                        onChange={toggleTodos}
                        aria-label="Selecionar todos"
                      />
                    </Cabecalho>
                    <Cabecalho>Contrato</Cabecalho>
                    <Cabecalho>Cliente</Cabecalho>
                    <Cabecalho>Tipo</Cabecalho>
                    <Cabecalho>Vigência</Cabecalho>
                    <Cabecalho>Vencimento</Cabecalho>
                    <Cabecalho>Termos</Cabecalho>
                    <Cabecalho>Situação</Cabecalho>
                  </LinhaDeCabecalho>
                  <tbody>
                    {contratos.map((contrato) => (
                      <LinhaDeContrato
                        key={contrato.id}
                        contrato={contrato}
                        selecionado={selecionados.has(contrato.id)}
                        onToggle={() => toggleSelecionado(contrato.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <DialogoDeContrato aberto={criando} aoFechar={() => setCriando(false)} />

      {selecionados.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-border bg-background px-5 py-3 shadow-2xl">
          <span className="text-sm font-medium tabular-nums text-muted-foreground">
            {selecionados.size} selecionado(s)
          </span>
          <Button
            size="sm"
            variant="secondary"
            loading={acaoEmLote.isPending}
            onClick={() => acaoEmLote.mutate('suspend')}
          >
            Suspender
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={acaoEmLote.isPending}
            onClick={() => acaoEmLote.mutate('resume')}
          >
            Retomar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelecionados(new Set())}
          >
            Desmarcar
          </Button>
        </div>
      )}
    </>
  );
}

function LinhaDeContrato({
  contrato,
  selecionado,
  onToggle,
}: {
  contrato: ContractSummary;
  selecionado: boolean;
  onToggle: () => void;
}) {
  const situacao = situacaoDeContrato(contrato.status);
  const inativo = contrato.status === 'TERMINATED' || contrato.status === 'EXPIRED';
  const tom = tomDeVencimento(contrato.diasParaVencer);

  return (
    <Linha inativo={inativo}>
      <Celula>
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-border"
          checked={selecionado}
          onChange={onToggle}
          aria-label={`Selecionar ${contrato.name}`}
          onClick={(e) => e.stopPropagation()}
        />
      </Celula>
      <Celula>
        <Link
          {...enderecoDoContrato(contrato.id)}
          className="font-medium text-foreground hover:text-primary focus-visible:shadow-glow focus-visible:outline-none"
        >
          {contrato.name}
        </Link>
        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
          {contrato.number}
          {/* O número do CLIENTE ao lado do interno: é por ele que se conversa
              sobre o contrato, e o interno não o substitui. */}
          {contrato.externalNumber ? ` · ${contrato.externalNumber}` : ''}
        </span>
      </Celula>
      <Celula>{contrato.accountName}</Celula>
      <Celula className="text-xs text-muted-foreground">
        {tipoDeContrato(contrato.type).rotulo}
      </Celula>
      <Celula className="text-xs tabular-nums text-muted-foreground">
        {contrato.vigenteDe
          ? `${formatarData(contrato.vigenteDe)} → ${
              contrato.vigenteAte ? formatarData(contrato.vigenteAte) : 'indeterminado'
            }`
          : '—'}
      </Celula>
      <Celula>
        <Etiqueta
          rotulo={textoDeVencimento(contrato.diasParaVencer, contrato.vigenteDe !== null)}
          tom={tom}
        />
      </Celula>
      <Celula className="tabular-nums text-xs">{contrato.termos}</Celula>
      <Celula>
        <Etiqueta rotulo={situacao.rotulo} tom={situacao.tom} titulo={situacao.origem} />
      </Celula>
    </Linha>
  );
}

